<div class="wrapper">
  <div class="management-heading">
    <div>
      <h2 class="mat-subtitle-1">Design layers</h2>
      <p class="mat-body-1">Add geographic data or drawings to the project map. Group related layers so crews can toggle them together.</p>
    </div>
    <div class="management-actions">
      @if (publishingService.canConfigure(projectId)) {
        <app-primary-button buttonId="map-add-layer" (handleClick)="create()">Add layer</app-primary-button>
      }
    </div>
  </div>

  @if (folderError) { <p class="error-message" role="alert">{{ folderError }}</p> }
  @if (registerError) { <p class="error-message" role="alert" data-testid="map-layer-register-error">{{ registerError }}</p> }

  <nav class="management-tabs" aria-label="Map layer lists">
    <button type="button" class="mat-body-1" [class.active]="activeTab === 'map'" [attr.aria-current]="activeTab === 'map' ? 'page' : null" (click)="activeTab = 'map'">On the map <span>{{ onMapCount }}</span></button>
    @if (showPendingTab) {
      <button type="button" class="mat-body-1" [class.active]="activeTab === 'review'" [attr.aria-current]="activeTab === 'review' ? 'page' : null" (click)="activeTab = 'review'">Pending <span>{{ pendingRows.length }}</span></button>
    }
  </nav>

  @if (activeTab === 'map') {
    <p class="section-help mat-body-1">These layers are available on the project map in web and mobile.</p>
    @if (catalogError) {
      <p role="alert">{{ catalogError }} <button mat-button type="button" color="primary" (click)="refresh()">Retry</button></p>
    } @else if (catalogLoading || loading) {
      <p role="status">Loading map layers…</p>
    } @else {
      @if (layerFolders.length) {
        @for (folder of layerFolders; track folder.id) {
          <section class="layer-folder" [attr.data-folder-id]="folder.id">
            <div class="folder-heading">
              <button type="button" class="folder-toggle" (click)="toggleFolder(folder.id)"
                [attr.aria-expanded]="!collapsedFolders.has(folder.id)" [attr.aria-controls]="'folder-content-' + folder.id">
                <mat-icon svgIcon="folder" class="folder-icon" />
                <span class="folder-title"><span class="mat-subtitle-1">{{ folder.name }}</span><span class="mat-caption">{{ folder.layers.length }} {{ folder.layers.length === 1 ? 'layer' : 'layers' }}</span></span>
                <mat-icon [svgIcon]="collapsedFolders.has(folder.id) ? 'chevron-down' : 'chevron-up'" class="chevron-icon" />
              </button>
              @if (publishingService.canConfigure(projectId)) {
                <button type="button" mat-icon-button [matMenuTriggerFor]="folderMenu" [attr.aria-label]="'Options for ' + folder.name" [disabled]="savingFolder"><mat-icon>more_horiz</mat-icon></button>
                <mat-menu #folderMenu="matMenu">
                  <button mat-menu-item (click)="create(folder.id)">Add layer to group</button>
                  @if (publishingService.canPublish(projectId)) {
                    <button mat-menu-item (click)="editFolder(folder)">Rename group</button>
                    <button mat-menu-item (click)="removeFolder(folder, $event)">Remove group</button>
                  }
                </mat-menu>
              }
            </div>
            <div [id]="'folder-content-' + folder.id" [hidden]="collapsedFolders.has(folder.id)">
              @if (folder.layers.length) { <ng-container *ngTemplateOutlet="layerTable; context: { $implicit: folder.layers }" /> }
              @else {
                <div class="folder-empty mat-body-1">No layers in this group yet.
                  @if (publishingService.canConfigure(projectId)) { <button type="button" mat-button color="primary" (click)="create(folder.id)">Add layer</button> }
                </div>
              }
            </div>
          </section>
        }
        @if (unfiledLayers.length) {
          <section class="unfiled-layers">
            <h3 class="mat-subtitle-1">Other map layers</h3>
            <ng-container *ngTemplateOutlet="layerTable; context: { $implicit: unfiledLayers }" />
          </section>
        }
      } @else if (unfiledLayers.length) {
        <ng-container *ngTemplateOutlet="layerTable; context: { $implicit: unfiledLayers }" />
      }
      @if (!onMapCount && !layerFolders.length) {
        <div class="empty-state"><h3 class="mat-subtitle-1">Start your project map</h3><p>Add a layer from a file or an ArcGIS service. Create groups to organize related layers.</p></div>
      }
    }
  } @else {
    <div class="imports-heading">
      <p class="mat-body-1">Layers wait here while they process, need a location, failed, or are new versions of a layer already on the map.</p>
      <div class="import-search"><label for="design-import-search" class="mat-caption">Search pending layers</label>
        <input id="design-import-search" type="search" class="import-search-input" maxlength="256" [(ngModel)]="importSearch" (ngModelChange)="onImportSearchChange()" />
      </div>
    </div>
    @if (importError) { <p role="alert">{{ importError }} <button type="button" mat-button color="primary" (click)="refreshImports()">Retry</button></p> }
    @if (pendingRows.length) {
      <div class="table-scroll-wrapper">
        <table mat-table [dataSource]="pendingRows" class="table">
          <ng-container matColumnDef="importName"><th mat-header-cell *matHeaderCellDef>Name</th><td mat-cell *matCellDef="let row"><button type="button" class="import-name-button" (click)="$event.stopPropagation(); openImport(row)" [disabled]="!row.latestVersion?.id || !publishingService.canConfigure(projectId)">{{ row.name }}</button></td></ng-container>
          <ng-container matColumnDef="importSource"><th mat-header-cell *matHeaderCellDef>Source</th><td mat-cell *matCellDef="let row">{{ importSourceLabel(row) }}</td></ng-container>
          <ng-container matColumnDef="importStatus"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let row">{{ sourceService.statusLabel(row.latestVersion?.status) }}</td></ng-container>
          <ng-container matColumnDef="importActions"><th mat-header-cell *matHeaderCellDef></th><td mat-cell *matCellDef="let row" class="actions-cell">
            <button type="button" mat-icon-button [matMenuTriggerFor]="importMenu" [attr.aria-label]="'Options for ' + row.name" [attr.data-testid]="'pending-row-menu'" [disabled]="!publishingService.canConfigure(projectId)" (click)="$event.stopPropagation()"><mat-icon>more_horiz</mat-icon></button>
            <mat-menu #importMenu="matMenu">
              <button mat-menu-item (click)="openImport(row)" [disabled]="!row.latestVersion?.id">Review layer</button>
              @if (registrableSource(row); as sourceId) {
                <button mat-menu-item (click)="registerAsDrawing(sourceId)" [disabled]="!!registeringSourceId" data-testid="pending-row-register-drawing">Register as drawing</button>
              }
              <button mat-menu-item (click)="removeImport(row, $event)" [disabled]="!canRemoveImport(row)" data-testid="pending-row-remove">Remove</button>
            </mat-menu>
          </td></ng-container>
          <tr mat-header-row *matHeaderRowDef="importColumns"></tr><tr mat-row *matRowDef="let row; columns: importColumns" (click)="openImport(row)" [class.highlighted-row]="row.id === selectedImportId"></tr>
        </table>
      </div>
    } @else if (!importsLoading && !importError) {
      <div class="empty-state">{{ nextImportOffset !== null ? 'No pending layers in these results. Load more to check the remaining sources.' : (importSearch ? 'No pending layers match your search.' : 'Nothing is pending. Choose Add layer to bring in new map data.') }}</div>
    }
    @if (importsLoading) { <p role="status">Loading pending layers…</p> }
    @if (nextImportOffset !== null) { <app-secondary-button (handleClick)="refreshImports(true)" [isDisabled]="importsLoading">Load more</app-secondary-button> }
  }
</div>

<ng-template #layerTable let-layers>
  <div class="table-scroll-wrapper">
    <table mat-table [dataSource]="layers" [trackBy]="trackLayer" class="table">
      <ng-container matColumnDef="name"><th mat-header-cell *matHeaderCellDef>Name</th><td mat-cell *matCellDef="let row"><button type="button" class="layer-name-button" (click)="$event.stopPropagation(); openLayer(row)" [disabled]="!publishingService.canConfigure(projectId)">{{ row.name }}</button></td></ng-container>
      <ng-container matColumnDef="source"><th mat-header-cell *matHeaderCellDef>Source</th><td mat-cell *matCellDef="let row">{{ layerSource(row) }}</td></ng-container>
      <ng-container matColumnDef="features"><th mat-header-cell *matHeaderCellDef>Features</th><td mat-cell *matCellDef="let row">{{ layerFeatureCount(row) }}</td></ng-container>
      <ng-container matColumnDef="actions"><th mat-header-cell *matHeaderCellDef></th><td mat-cell *matCellDef="let row" (click)="$event.stopPropagation()">
        <button type="button" mat-icon-button [matMenuTriggerFor]="layerMenu" [attr.aria-label]="'Options for ' + row.name" [disabled]="!publishingService.canConfigure(projectId) || savingFolder"><mat-icon>more_horiz</mat-icon></button>
        <mat-menu #layerMenu="matMenu">
          @if (row.published && publishingService.canPublish(projectId)) {
            <button mat-menu-item [matMenuTriggerFor]="moveMenu">Move to group</button>
            <mat-menu #moveMenu="matMenu">
              @for (folder of catalog?.groups || []; track folder.id) { <button mat-menu-item [disabled]="folder.id === row.published.groupId" (click)="moveLayer(row, folder.id)">{{ folder.name }}</button> }
              @if (catalog?.groups?.length) {
                <button mat-menu-item [disabled]="!isGrouped(row)" (click)="moveLayer(row, null)">No group</button>
                <div class="menu-divider" role="separator"></div>
              }
              <button mat-menu-item (click)="editFolder(undefined, row)">New group…</button>
            </mat-menu>
          }
          @if (registrableSource(row); as sourceId) {
            <button mat-menu-item (click)="registerAsDrawing(sourceId)" [disabled]="!!registeringSourceId" data-testid="layer-row-register-drawing">Register as drawing</button>
          }
          @if (newVersionLabel(row); as label) { <button mat-menu-item (click)="newVersion(row)" data-testid="layer-row-new-version">{{ label }}</button> }
          @if (row.published && publishingService.canPublish(projectId)) { <button mat-menu-item (click)="removeFromMap(row, $event)">Remove from map</button> }
          @if (row.reference) { <button mat-menu-item (click)="delete(row.reference, $event)">Remove from map</button> }
        </mat-menu>
      </td></ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr><tr mat-row *matRowDef="let row; columns: displayedColumns" (click)="openLayer(row)"></tr>
    </table>
  </div>
</ng-template>
